import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from './ui/PageHeader';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { Input, Textarea } from './ui/Input';
import { Select } from './ui/Select';
import { FormRow } from './ui/FormRow';
import { DataTable, type Column, type DataTableFilter } from './DataTable';
import { toast, confirmar } from '../lib/toast';
import {
  api,
  useCreateEntity,
  useDeleteEntity,
  useEntityList,
  useUpdateEntity,
  type ListResponse,
} from '../lib/useEntity';

type FieldType = 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'color';

export type AdminField = {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  /** Para selects que cargan opciones de otra tabla. */
  selectFrom?: { endpoint: string; valueKey: string; labelKey: string };
  defaultValue?: string | number | boolean;
  hint?: string;
  /** Si true, no se muestra en el formulario de creación (computed o auto). */
  hideCreate?: boolean;
  /** Si true, no se muestra en el formulario de edición. */
  hideUpdate?: boolean;
  /** Si true, columna en la tabla de listado. */
  list?: boolean;
  /** Con formColumns=2, el campo ocupa las dos columnas (textos largos). */
  wide?: boolean;
  /** Título de apartado del formulario: se pinta cuando cambia respecto al campo anterior. */
  section?: string;
};

type Props<T> = {
  title: string;
  subtitle?: string;
  /** Endpoint base para CRUD (POST/PUT/DELETE y GET cuando no hay listEndpoint). */
  endpoint: string;
  /** Endpoint alternativo solo para listar (típicamente uno enriquecido con
   *  joins/cálculos). Si no se pasa, se usa `endpoint`. */
  listEndpoint?: string;
  rowKey?: (row: T) => string;
  fields: AdminField[];
  /** Columnas personalizadas. Si no se pasan, se generan a partir de fields.list. */
  columns?: Column<T>[];
  /** Para entidades con PK no autogenerada (codigo, clave). */
  keyField?: string;
  /** Override del click en fila. Si está definido sustituye al editor inline.
   *  Útil para listados que llevan a página de detalle (proveedores, etc.). */
  onRowClick?: (row: T) => void;
  /** Texto adicional a mostrar antes de la tabla (info contextual, etc.). */
  headerExtra?: ReactNode;
  /** Si está, activa selección múltiple + botón "Fusionar N" que llama a
   *  POST <mergeEndpoint> con { master_id, ids: [otros] }. Tras éxito,
   *  refresca la lista. */
  mergeEndpoint?: string;
  /** Etiqueta singular ("subcategoría", "categoría") para los textos del
   *  modal de fusión. Default "registro". */
  mergeNoun?: string;
  /** Si está, activa selección múltiple + botón "Aplicar a N" que abre un modal
   *  con los campos de `bulkEditKeys` y llama a POST <bulkEditEndpoint> con
   *  { ids, cambios }. Solo se envían los campos que el usuario marque, así que
   *  se puede cambiar la gama de 30 filas sin tocar su categoría. */
  bulkEditEndpoint?: string;
  /** Keys de `fields` que se ofrecen en el modal de aplicación masiva. */
  bulkEditKeys?: string[];
  /** Filtros pasados al DataTable interno (mismo formato que en otros
   *  listados). Por ejemplo: filtrar subcategorías por categoría. */
  tableFilters?: DataTableFilter<T>[];
  /** Columnas del formulario de alta/edición. Con 2 la ventana es más ancha y
   *  menos alta (formularios con muchos campos, como condiciones comerciales). */
  formColumns?: 1 | 2;
};

export function AdminCrudPage<T extends Record<string, unknown>>({
  title,
  subtitle,
  endpoint,
  listEndpoint,
  fields,
  columns,
  rowKey,
  keyField = 'id',
  onRowClick,
  headerExtra,
  mergeEndpoint,
  mergeNoun = 'registro',
  bulkEditEndpoint,
  bulkEditKeys,
  tableFilters,
  formColumns = 1,
}: Props<T>) {
  const qc = useQueryClient();
  const list = useEntityList<T>(listEndpoint ?? endpoint);
  const create = useCreateEntity<T>(endpoint, listEndpoint);
  const update = useUpdateEntity<T>(endpoint, listEndpoint);
  const del = useDeleteEntity(endpoint, listEndpoint);

  const [editing, setEditing] = useState<T | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  // Selección múltiple + fusión
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeMasterId, setMergeMasterId] = useState<string>('');

  const merge = useMutation({
    mutationFn: (body: { master_id: string; ids: string[] }) =>
      api.post<{ ok: boolean; moved: Record<string, number> }>(mergeEndpoint!, body),
    onSuccess: (r) => {
      toast.success(
        'Fusión completada',
        `Movidos: ${Object.entries(r.moved).map(([k, v]) => `${k}=${v}`).join(', ')}`,
      );
      qc.invalidateQueries({ queryKey: [endpoint] });
      qc.invalidateQueries({ queryKey: [listEndpoint ?? endpoint] });
      qc.invalidateQueries({ queryKey: ['list', endpoint] });
      setSelectedIds(new Set());
      setMergeOpen(false);
      setMergeMasterId('');
    },
    onError: (err) => toast.error('No se pudo completar la fusión', (err as Error).message),
  });

  // Aplicación masiva. `bulkActivos` es qué campos se van a escribir: sin esto
  // no se podría distinguir "no toques la categoría" de "vacía la categoría".
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkActivos, setBulkActivos] = useState<Set<string>>(new Set());
  const [bulkValores, setBulkValores] = useState<Record<string, unknown>>({});

  const bulkFields = (bulkEditKeys ?? []).
    map((k) => fields.find((f) => f.key === k)).
    filter((f): f is AdminField => !!f);

  const cerrarBulk = () => { setBulkOpen(false); setBulkActivos(new Set()); setBulkValores({}); };

  const bulk = useMutation({
    mutationFn: (body: { ids: string[]; cambios: Record<string, unknown> }) =>
      api.post<{ ok: boolean; actualizadas: number }>(bulkEditEndpoint!, body),
    onSuccess: (r) => {
      toast.success(`Aplicado a ${r.actualizadas} fila(s)`);
      qc.invalidateQueries({ queryKey: [endpoint] });
      qc.invalidateQueries({ queryKey: [listEndpoint ?? endpoint] });
      qc.invalidateQueries({ queryKey: ['list', endpoint] });
      qc.invalidateQueries({ queryKey: ['list', listEndpoint ?? endpoint] });
      setSelectedIds(new Set());
      cerrarBulk();
    },
    onError: (err) => toast.error('No se aplicó nada', (err as Error).message),
  });

  const openCreate = () => {
    const initial: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.defaultValue !== undefined) initial[f.key] = f.defaultValue;
    }
    setFormData(initial);
    setCreating(true);
  };

  const openEdit = (row: T) => {
    setFormData({ ...row });
    setEditing(row);
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
    setFormData({});
  };

  const setField = (k: string, v: unknown) => setFormData((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    const payload = serialize(formData, fields);
    if (creating) {
      await create.mutateAsync(payload);
    } else if (editing) {
      const id = String((editing as Record<string, unknown>)[keyField]);
      await update.mutateAsync({ id, body: payload });
    }
    close();
  };

  const handleDelete = async (row: T) => {
    if (!(await confirmar({ title: '¿Eliminar este registro?', message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true }))) return;
    const id = String(row[keyField]);
    await del.mutateAsync(id);
  };

  const handleDeleteFromModal = async () => {
    if (!editing) return;
    if (!(await confirmar({ title: '¿Eliminar este registro?', message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true }))) return;
    const id = String((editing as Record<string, unknown>)[keyField]);
    await del.mutateAsync(id);
    close();
  };

  const tableCols: Column<T>[] = columns ?? buildColumnsFromFields<T>(fields);
  const actionsCol: Column<T> = {
    key: '__actions',
    label: '',
    sortable: false,
    align: 'right',
    render: (row) => (
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="edit"
          onClick={(e) => {
            e.stopPropagation();
            openEdit(row);
          }}
        >
          ✏️ Editar
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(row);
          }}
        >
          🗑 Eliminar
        </Button>
      </div>
    ),
  };

  const data = (list.data as ListResponse<T> | undefined)?.data ?? [];
  const open = creating || !!editing;
  const getRowId = rowKey ?? ((r: T) => String(r[keyField]));
  const seleccionados = useMemo(
    () => data.filter((r) => selectedIds.has(getRowId(r))),
    [data, selectedIds, getRowId],
  );

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {bulkEditEndpoint && bulkFields.length > 0 && selectedIds.size >= 1 && (
              <Button variant="edit" onClick={() => setBulkOpen(true)}>
                ✎ Aplicar a {selectedIds.size}
              </Button>
            )}
            {mergeEndpoint && selectedIds.size >= 2 && (
              <Button
                variant="edit"
                onClick={() => { setMergeMasterId(seleccionados[0] ? getRowId(seleccionados[0]) : ''); setMergeOpen(true); }}
              >
                ⚙ Fusionar {selectedIds.size} {mergeNoun}s
              </Button>
            )}
            <Button variant="create" onClick={openCreate}>+ Nuevo</Button>
          </>
        }
      />

      {headerExtra}

      {(mergeEndpoint || bulkEditEndpoint) && selectedIds.size > 0 && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <strong>{selectedIds.size}</strong> {mergeNoun}(s) seleccionado(s).
          {mergeEndpoint && selectedIds.size < 2 && ' (selecciona al menos 2 para poder fusionar)'}
          <button onClick={() => setSelectedIds(new Set())} className="ml-2 cursor-pointer text-xs underline">limpiar</button>
        </div>
      )}

      <DataTable<T>
        columns={[...tableCols, actionsCol]}
        rows={data}
        loading={list.isLoading}
        rowKey={getRowId}
        onRowClick={onRowClick ?? openEdit}
        defaultSort={tableCols[0] ? { key: tableCols[0].key } : undefined}
        selectable={!!mergeEndpoint || !!bulkEditEndpoint}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        filters={tableFilters}
      />

      <Modal
        open={open}
        onClose={close}
        title={creating ? `Nuevo · ${title}` : `Editar · ${title}`}
        size={formColumns === 2 ? 'xl' : 'md'}
        footer={
          <>
            {/* Eliminar a la izquierda, separado del resto; solo en edit */}
            {editing && (
              <Button
                variant="danger"
                onClick={handleDeleteFromModal}
                disabled={del.isPending}
              >
                🗑 {del.isPending ? 'Eliminando…' : 'Eliminar'}
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="danger" onClick={close}>Cancelar</Button>
            <Button variant={creating ? 'create' : 'save'} onClick={submit} disabled={create.isPending || update.isPending}>
              {creating ? 'Crear' : (update.isPending ? 'Guardando…' : 'Guardar')}
            </Button>
          </>
        }
      >
        <div className={formColumns === 2 ? 'grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2' : 'space-y-4'}>
          {fields
            .filter((f) => (creating ? !f.hideCreate : !f.hideUpdate))
            .map((f, i, visibles) => (
              <Fragment key={f.key}>
                {f.section && f.section !== visibles[i - 1]?.section && (
                  <h3 className={`${formColumns === 2 ? 'sm:col-span-2' : ''} ${i > 0 ? 'mt-2' : ''} border-b border-slate-200 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-600`}>
                    {f.section}
                  </h3>
                )}
                <div className={formColumns === 2 && (f.wide || f.type === 'textarea') ? 'sm:col-span-2' : undefined}>
                  <FormRow label={f.label} hint={f.hint} required={f.required}>
                    {renderField(f, formData[f.key], (v) => setField(f.key, v))}
                  </FormRow>
                </div>
              </Fragment>
            ))}
        </div>
      </Modal>

      {/* Modal de aplicación masiva */}
      {bulkEditEndpoint && (
        <Modal
          open={bulkOpen}
          onClose={cerrarBulk}
          title={`Aplicar a ${selectedIds.size} fila(s)`}
          size="lg"
          footer={
            <>
              <Button variant="danger" onClick={cerrarBulk}>Cancelar</Button>
              <Button
                variant="save"
                disabled={bulkActivos.size === 0 || bulk.isPending}
                onClick={async () => {
                  const cambios: Record<string, unknown> = {};
                  for (const k of bulkActivos) {
                    const v = bulkValores[k];
                    cambios[k] = v === '' || v === undefined ? null : v;
                  }
                  const resumen = bulkFields
                    .filter((f) => bulkActivos.has(f.key))
                    .map((f) => `· ${f.label}: ${cambios[f.key] == null ? '(vaciar)' : 'el valor elegido'}`)
                    .join('\n');
                  if (!(await confirmar({ title: `¿Cambiar ${selectedIds.size} fila(s)?`, message: `${resumen}\n\nEl resto de campos se queda como está.`, confirmLabel: 'Aplicar', danger: true }))) return;
                  bulk.mutate({ ids: Array.from(selectedIds), cambios });
                }}
              >
                {bulk.isPending ? 'Aplicando…' : `Aplicar a ${selectedIds.size}`}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Marca la casilla de los campos que quieras cambiar. Los que dejes sin marcar
              <strong> no se tocan</strong> en ninguna de las filas seleccionadas. Un campo marcado
              y dejado en blanco <strong>vacía</strong> ese campo.
            </p>
            {bulkFields.map((f) => {
              const activo = bulkActivos.has(f.key);
              return (
                <div key={f.key} className={`rounded-md border px-3 py-2 ${activo ? 'border-sky-300 bg-sky-50' : 'border-slate-200'}`}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={activo}
                      onChange={(e) => {
                        setBulkActivos((s) => {
                          const n = new Set(s);
                          if (e.target.checked) n.add(f.key); else n.delete(f.key);
                          return n;
                        });
                      }}
                    />
                    {f.label}
                  </label>
                  {activo && (
                    <div className="mt-2">
                      {renderField(f, bulkValores[f.key], (v) => setBulkValores((s) => ({ ...s, [f.key]: v })))}
                      {f.hint && <p className="mt-1 text-xs text-slate-500">{f.hint}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {/* Modal de fusión */}
      {mergeEndpoint && (
        <Modal
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          title={`Fusionar ${selectedIds.size} ${mergeNoun}s`}
          size="lg"
          footer={
            <>
              <Button variant="danger" onClick={() => setMergeOpen(false)}>Cancelar</Button>
              <Button
                variant="save"
                onClick={async () => {
                  if (!(await confirmar({ title: `¿Fusionar ${mergeNoun}s?`, message: `Los ${mergeNoun}s no-master quedarán soft-deleted y sus productos asociados pasarán al master.`, confirmLabel: 'Fusionar', danger: true }))) return;
                  const ids = Array.from(selectedIds).filter((id) => id !== mergeMasterId);
                  merge.mutate({ master_id: mergeMasterId, ids });
                }}
                disabled={!mergeMasterId || merge.isPending}
              >
                {merge.isPending ? 'Fusionando…' : 'Fusionar al master'}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Elige el {mergeNoun} que se mantendrá como <strong>MASTER</strong>. El resto se elimina
              y sus productos asociados pasan al master.
            </p>
            <FormRow label="Master" required>
              <Select
                options={seleccionados.map((row) => ({
                  value: getRowId(row),
                  label: String(row.nombre ?? row.codigo ?? getRowId(row)),
                }))}
                value={mergeMasterId}
                emptyLabel="— elegir —"
                onChange={(e) => setMergeMasterId(e.target.value)}
              />
            </FormRow>
            <ul className="space-y-1 rounded border border-slate-200 p-3 text-sm">
              {seleccionados.map((row) => {
                const rid = getRowId(row);
                return (
                  <li key={rid} className="flex items-center justify-between">
                    <span>{String(row.nombre ?? row.codigo ?? rid)}</span>
                    {rid === mergeMasterId ? (
                      <span className="text-xs font-semibold text-emerald-700">★ MASTER</span>
                    ) : (
                      <span className="text-xs text-rose-600">se fusionará</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DynamicSelect({
  endpoint,
  valueKey,
  labelKey,
  value,
  onChange,
}: {
  endpoint: string;
  valueKey: string;
  labelKey: string;
  value: string;
  onChange: (v: string | null) => void;
}) {
  const list = useEntityList<Record<string, unknown>>(endpoint);
  const options = (list.data?.data ?? []).map((row) => ({
    value: String(row[valueKey] ?? ''),
    label: String(row[labelKey] ?? row[valueKey] ?? ''),
  }));
  return (
    <Select
      options={options}
      value={value}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

/** Convierte un value de BD a string para el form. Si es array (text[] de
 *  PG) lo devuelve como CSV — el backend lo re-convierte a array al
 *  guardar gracias a middlewares específicos por router. */
function toFormString(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function renderField(f: AdminField, value: unknown, onChange: (v: unknown) => void): ReactNode {
  if (f.selectFrom) {
    return (
      <DynamicSelect
        endpoint={f.selectFrom.endpoint}
        valueKey={f.selectFrom.valueKey}
        labelKey={f.selectFrom.labelKey}
        value={toFormString(value)}
        onChange={onChange}
      />
    );
  }
  switch (f.type) {
    case 'textarea':
      return <Textarea value={toFormString(value)} onChange={(e) => onChange(e.target.value)} />;
    case 'number':
      return (
        <Input
          type="number"
          step="any"
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-slate-900 focus:ring-slate-500"
          />
          <span className="text-sm text-slate-700">Sí</span>
        </label>
      );
    case 'select':
      return (
        <Select
          options={f.options ?? []}
          value={toFormString(value)}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'color':
      return (
        <Input
          type="color"
          value={(value as string) ?? '#64748b'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-20 p-1 cursor-pointer"
        />
      );
    default:
      return (
        <Input
          value={toFormString(value)}
          onChange={(e) => onChange(e.target.value)}
          required={f.required}
        />
      );
  }
}

function serialize(formData: Record<string, unknown>, fields: AdminField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = formData[f.key];
    if (v === '' || v === undefined) {
      out[f.key] = null;
    } else {
      out[f.key] = v;
    }
  }
  return out;
}

function buildColumnsFromFields<T extends Record<string, unknown>>(fields: AdminField[]): Column<T>[] {
  return fields
    .filter((f) => f.list !== false && f.type !== 'textarea')
    .map((f) => {
      const col: Column<T> = {
        key: f.key,
        label: f.label,
        align: f.type === 'number' ? 'right' : 'left',
      };
      if (f.type === 'boolean') {
        col.render = (row) => ((row[f.key] as boolean) ? 'Sí' : 'No');
      } else {
        // Fallback genérico — convierte arrays a CSV para no mostrar
        // "[object Array]" cuando una columna mapea a un text[] de PG.
        col.render = (row) => {
          const v = row[f.key];
          if (Array.isArray(v)) return v.join(', ');
          if (v == null) return '';
          if (typeof v === 'object') return JSON.stringify(v);
          return String(v);
        };
      }
      if (f.type === 'color') {
        col.render = (row) => {
          const color = (row[f.key] as string) ?? '#64748b';
          return (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-4 w-4 rounded-full ring-1 ring-slate-200"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-slate-500">{color}</span>
            </span>
          );
        };
      }
      return col;
    });
}
